import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Download,
  Loader2,
  Pencil,
  Phone,
  Plus,
  Save,
  Trash2,
  TriangleAlert,
  Upload,
  Users,
  X,
} from 'lucide-react';

import { chatCapabilitiesAPI, complaintTicketsAPI, indicatorsAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from '../ui/use-toast';

type Indicator = {
  id: number;
  programme: string;
  indicator_name: string;
  category?: string | null;
  description?: string | null;
  source: string;
  persona_name?: string | null;
  assignee_pool_user_ids?: number[];
  configured_category_id?: string | null;
};

type AgentOption = {
  id: number;
  name: string;
  email: string;
  phone_number?: string | null;
  notify_eligible: boolean;
};

type ManageDraft = { persona_name: string; assignee_pool_user_ids: number[] };
const emptyManageDraft: ManageDraft = { persona_name: '', assignee_pool_user_ids: [] };

type ImportRowResult = {
  row: number;
  status: 'inserted' | 'updated' | 'skipped' | 'error';
  detail: string;
  programme?: string | null;
  indicator_name?: string | null;
};

type ImportResult = {
  inserted: number;
  updated: number;
  skipped: number;
  errors: number;
  rows: ImportRowResult[];
};

const CSV_TEMPLATE =
  'programme,indicator_name,category,description\n' +
  'TB,Case notification rate,Coverage,New and relapse TB cases notified per 100 000 population\n';

const emptyDraft = { programme: '', indicator_name: '', category: '', description: '' };

const IndicatorCatalogueSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [indicators, setIndicators] = useState<Indicator[]>([]);
  // 2026-08-09: set right after a create/update whose response includes a
  // configured_category_id — surfaces a "configure this now" CTA instead of
  // leaving the admin to go find the new category in Digital Companion
  // themselves (previously just a toast mentioning auto-population).
  const [justConfigured, setJustConfigured] = useState<{ categoryId: string; label: string } | null>(
    null,
  );
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [manageId, setManageId] = useState<number | null>(null);
  const [manageDraft, setManageDraft] = useState<ManageDraft>(emptyManageDraft);
  const [managing, setManaging] = useState(false);
  const [matchingEnabled, setMatchingEnabled] = useState(false);
  const [matchingLoading, setMatchingLoading] = useState(true);
  const [matchingSaving, setMatchingSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, options] = await Promise.all([
        indicatorsAPI.list({ q: search || undefined, limit: 500 }),
        complaintTicketsAPI.getConfigOptions(),
      ]);
      setIndicators(rows);
      setAgents(options.agents || []);
    } catch (error: any) {
      toast({
        title: 'Could not load indicators',
        description: error?.response?.data?.detail || 'Administrator access is required.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatCapabilitiesAPI
      .get()
      .then((res) => setMatchingEnabled(!!res.settings?.indicator_catalogue_enabled))
      .catch(() => {
        // Leave the default (off) — the toggle itself will surface a real
        // error on the next attempted change.
      })
      .finally(() => setMatchingLoading(false));
  }, []);

  const toggleMatching = async (checked: boolean) => {
    setMatchingSaving(true);
    try {
      const res = await chatCapabilitiesAPI.update({ indicator_catalogue_enabled: checked });
      setMatchingEnabled(!!res.settings?.indicator_catalogue_enabled);
      toast({
        title: checked ? 'Indicator matching enabled' : 'Indicator matching disabled',
        description: checked
          ? 'The bot can now call match_indicator, and new indicators will auto-populate Configured Experiences.'
          : 'The bot will no longer call match_indicator, and new indicators will stop auto-populating Configured Experiences.',
      });
    } catch (error: any) {
      toast({
        title: 'Could not update this setting',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setMatchingSaving(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load();
  };

  const startAdd = () => {
    setDraft(emptyDraft);
    setAdding(true);
  };

  const saveAdd = async () => {
    if (!draft.programme.trim() || !draft.indicator_name.trim()) {
      toast({
        title: 'Programme and indicator name are required',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const created = await indicatorsAPI.create({
        programme: draft.programme.trim(),
        indicator_name: draft.indicator_name.trim(),
        category: draft.category.trim() || undefined,
        description: draft.description.trim() || undefined,
      });
      setAdding(false);
      setDraft(emptyDraft);
      await load();
      toast({ title: 'Indicator added' });
      if (created?.configured_category_id) {
        setJustConfigured({
          categoryId: created.configured_category_id,
          label: created.indicator_name,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Could not add indicator',
        description: error?.response?.data?.detail || 'Please check the values.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: Indicator) => {
    setEditingId(row.id);
    setEditDraft({
      programme: row.programme,
      indicator_name: row.indicator_name,
      category: row.category || '',
      description: row.description || '',
    });
  };

  const saveEdit = async (id: number) => {
    setSaving(true);
    try {
      const updated = await indicatorsAPI.update(id, {
        programme: editDraft.programme.trim(),
        indicator_name: editDraft.indicator_name.trim(),
        category: editDraft.category.trim() || undefined,
        description: editDraft.description.trim() || undefined,
      });
      setEditingId(null);
      await load();
      toast({ title: 'Indicator updated' });
      if (updated?.configured_category_id) {
        setJustConfigured({
          categoryId: updated.configured_category_id,
          label: updated.indicator_name,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Could not save changes',
        description: error?.response?.data?.detail || 'Please check the values.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: Indicator) => {
    if (!window.confirm(`Delete indicator "${row.indicator_name}"?`)) return;
    try {
      await indicatorsAPI.remove(row.id);
      await load();
      toast({ title: 'Indicator deleted' });
    } catch (error: any) {
      toast({
        title: 'Could not delete indicator',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    }
  };

  const toggleManage = (row: Indicator) => {
    if (manageId === row.id) {
      setManageId(null);
      return;
    }
    setManageId(row.id);
    setManageDraft({
      persona_name: row.persona_name || '',
      assignee_pool_user_ids: row.assignee_pool_user_ids || [],
    });
  };

  const toggleManageAgent = (agentId: number, checked: boolean) => {
    setManageDraft((prev) => {
      const pool = new Set(prev.assignee_pool_user_ids);
      if (checked) pool.add(agentId);
      else pool.delete(agentId);
      return { ...prev, assignee_pool_user_ids: Array.from(pool) };
    });
  };

  const saveManage = async (id: number) => {
    setManaging(true);
    try {
      await indicatorsAPI.update(id, {
        persona_name: manageDraft.persona_name.trim() || undefined,
        assignee_pool_user_ids: manageDraft.assignee_pool_user_ids,
      });
      setManageId(null);
      await load();
      toast({ title: 'Community management updated' });
    } catch (error: any) {
      toast({
        title: 'Could not save community management',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setManaging(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'indicator_catalogue_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await indicatorsAPI.import(file);
      setImportResult(result);
      await load();
      toast({
        title: 'Import finished',
        description: `${result.inserted} inserted, ${result.updated} updated, ${result.skipped} skipped, ${result.errors} errors.`,
      });
    } catch (error: any) {
      toast({
        title: 'Import failed',
        description: error?.response?.data?.detail || 'Please check the file format.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Indicator Catalogue</CardTitle>
          <CardDescription>
            Programme indicators (e.g. CLM indicators) for this account. Upload CSV/JSON to
            bulk-add, or edit rows below — re-uploading the same programme + indicator name
            updates that row instead of duplicating it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {justConfigured && (
            <Alert className="flex items-center justify-between gap-3">
              <AlertDescription className="flex-1">
                "{justConfigured.label}" auto-populated a category in Digital Companion —
                it's inactive until you configure and activate it there.
              </AlertDescription>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/dashboard/settings?tab=9&category=${encodeURIComponent(justConfigured.categoryId)}`,
                    )
                  }
                >
                  Configure this category <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setJustConfigured(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </Alert>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={downloadTemplate}>
              <Download className="h-4 w-4" /> Download CSV template
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload CSV or JSON
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json,text/csv,application/json"
              className="hidden"
              onChange={handleFileChosen}
            />
            <Button size="sm" onClick={startAdd} className="ml-auto">
              <Plus className="h-4 w-4" /> Add indicator
            </Button>
          </div>

          {importResult && (
            <Alert variant={importResult.errors > 0 ? 'destructive' : undefined}>
              <TriangleAlert className="h-4 w-4" />
              <AlertDescription>
                <p className="mb-1">
                  {importResult.inserted} inserted · {importResult.updated} updated ·{' '}
                  {importResult.skipped} skipped · {importResult.errors} errors
                </p>
                {(importResult.errors > 0 || importResult.skipped > 0) && (
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-xs">
                    {importResult.rows
                      .filter((r) => r.status === 'error' || r.status === 'skipped')
                      .map((r) => (
                        <li key={r.row}>
                          Row {r.row}: {r.status} — {r.detail}
                        </li>
                      ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              placeholder="Search by name, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit" variant="outline" size="sm">
              Search
            </Button>
          </form>

          {adding && (
            <div className="grid gap-2 rounded-lg border p-3 md:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <Input
                placeholder="Programme"
                value={draft.programme}
                onChange={(e) => setDraft({ ...draft, programme: e.target.value })}
              />
              <Input
                placeholder="Indicator name"
                value={draft.indicator_name}
                onChange={(e) => setDraft({ ...draft, indicator_name: e.target.value })}
              />
              <Input
                placeholder="Category"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              />
              <Input
                placeholder="Description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
              <div className="flex items-center gap-1">
                <Button size="icon" onClick={saveAdd} disabled={saving} aria-label="Save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setAdding(false)}
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[160px] items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : indicators.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No indicators yet. Add one or upload a file to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Programme</TableHead>
                  <TableHead>Indicator</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {indicators.map((row) => {
                  const pool = row.assignee_pool_user_ids || [];
                  const poolMissingPhone = pool
                    .map((id) => agents.find((a) => a.id === id))
                    .filter((agent): agent is AgentOption => !!agent && !agent.notify_eligible);
                  return (
                    <React.Fragment key={row.id}>
                      {editingId === row.id ? (
                        <TableRow>
                          <TableCell>
                            <Input
                              value={editDraft.programme}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, programme: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.indicator_name}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, indicator_name: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.category}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, category: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.description}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, description: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {pool.length ? `${pool.length} agent(s)` : 'Not assigned'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{row.source}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                onClick={() => saveEdit(row.id)}
                                disabled={saving}
                                aria-label="Save"
                              >
                                {saving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setEditingId(null)}
                                aria-label="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell>{row.programme}</TableCell>
                          <TableCell className="font-medium">{row.indicator_name}</TableCell>
                          <TableCell>{row.category || '—'}</TableCell>
                          <TableCell className="max-w-xs truncate" title={row.description || ''}>
                            {row.description || '—'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {pool.length || row.persona_name ? (
                              <div className="space-y-0.5">
                                {row.persona_name && <div>{row.persona_name}</div>}
                                <div className="text-muted-foreground">
                                  {pool.length ? `${pool.length} agent(s)` : 'No agents'}
                                  {poolMissingPhone.length > 0 && (
                                    <span className="text-amber-600"> · {poolMissingPhone.length} missing phone</span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Not assigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{row.source}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => toggleManage(row)}
                                aria-label="Community management"
                                title="Community management — persona and owning agents"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => startEdit(row)}
                                aria-label="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => remove(row)}
                                aria-label="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {manageId === row.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/30">
                            <div className="space-y-3 p-2">
                              <div>
                                <p className="text-sm font-medium">
                                  Community management — {row.indicator_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Who follows up when this indicator is matched, and which
                                  persona the bot uses while discussing it.
                                </p>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Persona name</Label>
                                <Input
                                  value={manageDraft.persona_name}
                                  onChange={(e) =>
                                    setManageDraft((prev) => ({ ...prev, persona_name: e.target.value }))
                                  }
                                  placeholder="e.g. Community Health Focal Point"
                                  className="max-w-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Owning agents</Label>
                                <div className="grid max-h-56 gap-1 overflow-y-auto rounded-md border bg-background p-2 md:grid-cols-2">
                                  {agents.map((agent) => {
                                    const checked = manageDraft.assignee_pool_user_ids.includes(agent.id);
                                    return (
                                      <label
                                        key={agent.id}
                                        className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50"
                                      >
                                        <Checkbox
                                          checked={checked}
                                          onCheckedChange={(v) => toggleManageAgent(agent.id, v === true)}
                                        />
                                        <span className="truncate">{agent.name}</span>
                                        {!agent.notify_eligible && (
                                          <span title="No phone number on file">
                                            <Phone className="h-3 w-3 shrink-0 text-amber-600" />
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                  {agents.length === 0 && (
                                    <p className="text-xs text-muted-foreground">No agents available.</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => setManageId(null)}>
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={() => saveManage(row.id)} disabled={managing}>
                                  {managing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Save className="h-4 w-4" />
                                  )}
                                  Save
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Matching</CardTitle>
              <CardDescription className="mt-1">
                Lets the bot call a <code>match_indicator</code> tool to look up entries
                here against a user's question — separate from document knowledge base
                search. Also gates whether new indicators auto-populate Configured
                Experiences above. Use the <Users className="inline h-3 w-3" /> icon on a
                row to set who owns follow-up on that indicator and which persona the bot
                uses while discussing it.
              </CardDescription>
            </div>
            {matchingLoading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={matchingEnabled}
                onCheckedChange={toggleMatching}
                disabled={matchingSaving}
                aria-label="Enable indicator matching"
              />
            )}
          </div>
        </CardHeader>
      </Card>
    </div>
  );
};

export default IndicatorCatalogueSettings;
